import { useTheme } from '@mui/material/styles';
import { Area, AreaChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { sdsGrayscale } from '../../../theme/sds-tokens';
import {
  chartCardSurfaceDark,
  chartMutedTextColor,
  chartSubtitleColor,
  chartTitleColor,
  chartTrendColor,
} from './chartColors';

interface ChartItem {
  date: string;
  count: number;
}

const formatDate = (value: string) => {
  const parts = value.split('-');
  const month = parts[1];
  if (parts.length === 3) {
    return `${parts[2]}/${month}`;
  }
  return `${month}/${parts[0]}`;
};

export const DayAndMonthMedicalCertificate = ({ data }: { data: ChartItem[] }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';

  const mutedText = chartMutedTextColor(isDark);
  const surface = isDark ? chartCardSurfaceDark : sdsGrayscale[0];
  const maxCount = Math.max(...data.map(item => item.count), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <p style={{ color: chartTitleColor, fontSize: 16, fontWeight: 700, margin: '0 0 4px 0', padding: '1px' }}>
            Quantidade de atestados com risco psicossocial
          </p>
          <span style={{ color: chartSubtitleColor, fontSize: 13, fontWeight: 400 }}>
            Número de atestados por dia (DIA/MÊS)
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <div style={{ width: 14, height: 3, backgroundColor: chartTrendColor, borderRadius: 2 }} />
          <span style={{ color: mutedText, fontSize: 12, fontWeight: 600 }}>Atestados</span>
        </div>
      </div>

      <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 240, marginTop: 16 }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 28, right: 16, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="day-and-month-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartTrendColor} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={chartTrendColor} stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} stroke={palette.divider} />

              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tickLine={false}
                axisLine={{ stroke: palette.divider }}
                tick={{ fontSize: 12, fill: mutedText }}
                padding={{ left: 24, right: 24 }}
              />

              <YAxis
                domain={[0, maxCount]}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: mutedText }}
              />

              <Tooltip formatter={(value: number) => [`${value}`, 'Atestados']} />

              <Area
                type="monotone"
                dataKey="count"
                name="Atestados"
                stroke={chartTrendColor}
                strokeWidth={2}
                fill="url(#day-and-month-fill)"
                dot={{ r: 5, fill: surface, stroke: chartTrendColor, strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              >
                <LabelList
                  dataKey="count"
                  position="top"
                  offset={12}
                  style={{ fontSize: 12, fontWeight: 700, fill: palette.text.primary }}
                />
              </Area>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
